'use client'

import { Icon } from '@components/primitives/Icon'
import { classMerge } from '@components/utilities/classMerge'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown'
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp'
import { trackEvent } from '@lib/tracking'
import type { TrackingEvents } from '@lib/tracking/TrackingEvents'
import React, {
  type MutableRefObject,
  useEffect,
  useRef,
  useState,
} from 'react'

const useTruncatedElement = ({
  ref,
  title,
  trackingEvent,
}: {
  ref?: MutableRefObject<HTMLDivElement | null>
  title: React.ReactNode
  trackingEvent?: TrackingEvents
}) => {
  const [isTruncated, setIsTruncated] = useState(false)
  const [isShowingMore, setIsShowingMore] = useState(false)

  useEffect(() => {
    const { offsetHeight, scrollHeight } = ref?.current ?? {}

    if (offsetHeight && scrollHeight && offsetHeight < scrollHeight) {
      setIsTruncated(true)
    } else {
      setIsTruncated(false)
    }
  }, [ref])

  const toggleIsShowingMore = () => {
    if (trackingEvent) {
      trackEvent(trackingEvent, {
        val: title?.toString(),
      })
    }
    setIsShowingMore((prev) => !prev)
  }
  return {
    isShowingMore,
    isTruncated,
    toggleIsShowingMore,
  }
}

export const SeeMoreSection = ({
  children,
  className,
  title,
  trackingEvent,
}: {
  children: React.ReactNode
  className?: string
  title: React.ReactNode
  trackingEvent?: TrackingEvents
}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const { isShowingMore, isTruncated, toggleIsShowingMore } =
    useTruncatedElement({
      ref,
      title,
      trackingEvent,
    })

  return (
    <div
      className={classMerge('cursor-pointer hover:bg-neutral100', className)}
      onClick={toggleIsShowingMore}
    >
      <div className='flex items-center py-1'>{title}</div>
      {
        <div
          className={`break-words text-xl ${!isShowingMore && 'line-clamp-3 md:line-clamp-2'}`}
          ref={ref}
        >
          {children}
        </div>
      }
      {isTruncated && (
        <p className='mt-2 flex w-full items-center gap-1 text-primary800'>
          {isShowingMore ? 'Show less' : 'Show more'}
          <Icon
            className='mr-4 cursor-pointer text-neutral600'
            icon={isShowingMore ? faChevronUp : faChevronDown}
          />
        </p>
      )}
    </div>
  )
}
